import { cn } from '@/lib/cn';
import { For, type JSX, Show, createUniqueId } from 'solid-js';

export interface RadioOption {
  value: string;
  label: JSX.Element | string;
}

export interface RadioProps {
  options: readonly RadioOption[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  class?: string;
}

export const Radio = (props: RadioProps): JSX.Element => {
  const name = `radio-${createUniqueId()}`;
  return (
    <fieldset class={cn('flex flex-col gap-2 text-sm', props.class)} disabled={props.disabled}>
      <Show when={props.label}>
        <legend
          class={cn(
            'mb-1 font-medium text-neutral-700 dark:text-neutral-300',
            props.required && "after:ml-0.5 after:text-red-500 after:content-['*']",
          )}
        >
          {props.label}
        </legend>
      </Show>
      <For each={props.options}>
        {(option) => (
          <label class="relative inline-flex cursor-pointer items-center gap-2 disabled:cursor-not-allowed">
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={props.value === option.value}
              onChange={() => props.onChange(option.value)}
              class="peer sr-only"
            />
            <span
              aria-hidden
              class={cn(
                'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border bg-white transition-colors motion-reduce:transition-none',
                'border-neutral-300 dark:border-neutral-600 dark:bg-neutral-900',
                'peer-checked:border-brand-600 peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-1',
              )}
            >
              <span
                class={cn(
                  'h-2 w-2 rounded-full bg-brand-600 transition-opacity motion-reduce:transition-none',
                  props.value === option.value ? 'opacity-100' : 'opacity-0',
                )}
              />
            </span>
            {option.label}
          </label>
        )}
      </For>
      <Show when={props.error}>
        <span class="text-xs text-red-600">{props.error}</span>
      </Show>
      <Show when={!props.error && props.hint}>
        <span class="text-xs text-neutral-500">{props.hint}</span>
      </Show>
    </fieldset>
  );
};
